import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * LeaderboardWidget
 *
 * Compact top-5 leaderboard for the Dashboard. Fetches from
 * /api/dashboard/leaderboard. Current metric is "messages sent this
 * quarter" — the endpoint returns metric + period so we can display
 * them without hardcoding.
 *
 * If the current user is outside the top 5, the endpoint also returns
 * their rank+count as `me`; we surface that in a small footer row so
 * they always know where they stand.
 */
export default function LeaderboardWidget() {
  const [data, setData] = useState({ top: [], me: null, metric: 'messages_sent', period: 'quarter' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/dashboard/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          console.error('[banyak-leaderboard] fetch failed', { status: res.status, body: body.slice(0, 200) });
          setError('Failed to load');
          return;
        }
        const json = await res.json();
        setData(json);
      } catch (e) {
        console.error('[banyak-leaderboard] fetch threw', e);
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const metricLabel = data.metric === 'messages_sent' ? 'Messages' : data.metric;
  const periodLabel = data.period === 'quarter' ? 'This Quarter'
                    : data.period === 'month'   ? 'This Month'
                    : data.period;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Leaderboard
        </h3>
        <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">
          {metricLabel} · {periodLabel}
        </span>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400">Loading…</div>
      ) : error ? (
        <div className="py-6 text-center text-xs text-gray-400">Couldn't load leaderboard</div>
      ) : data.top.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400">No activity yet this quarter</div>
      ) : (
        <>
          <div className="space-y-1.5">
            {data.top.map((u) => (
              <div key={u.id} className="flex items-center gap-2.5">
                <span className={`w-5 text-xs font-bold text-center ${
                  u.rank === 1 ? 'text-amber-500' :
                  u.rank === 2 ? 'text-gray-400' :
                  u.rank === 3 ? 'text-amber-700' : 'text-gray-400'
                }`}>{u.rank}</span>
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <span className="text-sm text-gray-900 flex-1 truncate">{u.name}</span>
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                  {u.messages_sent || 0}
                </span>
              </div>
            ))}
          </div>

          {data.me && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-2.5 text-gray-500">
                <span className="w-5 text-xs font-bold text-center">{data.me.rank}</span>
                {data.me.avatar_url ? (
                  <img src={data.me.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 opacity-80" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {data.me.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <span className="text-sm flex-1 truncate">You · {data.me.name}</span>
                <span className="text-sm font-semibold tabular-nums">{data.me.messages_sent || 0}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
